import React from 'react';
import { isBoolean } from 'lodash';
import { CloseButton } from '@fiverr-private/button';
import { useHover } from '@fiverr-private/hooks';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Text, Typography } from '@fiverr-private/typography';
import { Avatar, AvatarImage, AvatarOnlineIndicator } from '@fiverr-private/avatar';
import { Image } from '@fiverr-private/media';
import { getContext } from '@fiverr-private/fiverr_context';
import { JobsTbdTitleBadgeMapper } from '../../../lib/utils/jobsTBDMapper';
import { JobTbdPageProps } from './JobTbdPage.types';
import {
    jobImageHeightAndWidth,
    jobImageHeightAndWidthLarge,
    pageStyle,
    subtitleStyle,
    sideNoteStyle,
} from './JobTbdPage.ve.css';
import { textCommonProps } from './constants';

export const JobTbdPage = (props: JobTbdPageProps): JSX.Element => {
    const { headerText, sellerData, title, subtitle, iconUrl, sideNote, cta, titleBadge, dismissInfo } = props;
    const { isMobile, isTouch } = getContext();
    const { hovered, onMouseEnter, onMouseLeave } = useHover();
    const { isDismissible, onDismiss } = dismissInfo;

    const Badge = titleBadge ? JobsTbdTitleBadgeMapper[titleBadge] : null;

    return (
        <Stack
            className={pageStyle}
            direction="column"
            gap={{
                default: '2',
                sm: '3',
            }}
            data-testid="jtbd-page"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Stack justifyContent="spaceBetween" alignItems="center" position="relative">
                <Typography
                    letterSpacing="0.6px"
                    size="b_xs"
                    fontWeight="semibold"
                    {...textCommonProps}
                    color="grey_900"
                >
                    {headerText}
                </Typography>
                {isDismissible && (
                    <Container
                        zIndex={'2'}
                        position={'absolute'}
                        right={-5}
                        top={-5}
                        height={'24px'}
                        opacity={{ default: isTouch || hovered ? '1' : '0' }}
                    >
                        <CloseButton size={'xs'} onClick={onDismiss} />
                    </Container>
                )}
            </Stack>
            <Stack alignItems={{ default: 'flexStart', sm: 'center' }}>
                {sellerData?.hasAvatar ? (
                    <Avatar
                        size={isMobile ? 'sm' : 'md'}
                        username={sellerData.username}
                        displayName={sellerData.fullName}
                    >
                        <AvatarImage src={iconUrl} alt="Job Page Avatar" />
                        {isBoolean(sellerData.isOnline) && (
                            <AvatarOnlineIndicator
                                position="bottom-right"
                                isOnline={sellerData.isOnline}
                                dataTestId={'AvatarOnlineIndicator'}
                            />
                        )}
                    </Avatar>
                ) : (
                    <Image
                        src={iconUrl}
                        alt="Job Page Image"
                        height={{ default: jobImageHeightAndWidth, sm: jobImageHeightAndWidthLarge }}
                        width={{ default: jobImageHeightAndWidth, sm: jobImageHeightAndWidthLarge }}
                        minWidth={{ default: jobImageHeightAndWidth, sm: jobImageHeightAndWidthLarge }}
                    />
                )}
                <Stack direction="row" gap="0" overflow="hidden" width="100%" justifyContent="spaceBetween">
                    <Stack direction="column" gap="0" overflow="hidden" width="100%">
                        <Stack direction="row" gap="2" alignItems="center">
                            <Text size="b_md" fontWeight="semibold" {...textCommonProps} color="bodyPrimary">
                                {title}
                            </Text>
                            {Badge && <Badge />}
                        </Stack>
                        <Stack justifyContent="spaceBetween" alignItems="center">
                            <Text
                                className={subtitleStyle}
                                size="b_sm"
                                fontWeight="normal"
                                {...textCommonProps}
                                color="bodyPrimary"
                            >
                                {subtitle}
                            </Text>
                            {sideNote && !cta && (
                                <Text
                                    size="b_xs"
                                    textAlign="end"
                                    className={sideNoteStyle}
                                    {...textCommonProps}
                                    color="bodyPrimary"
                                >
                                    {sideNote}
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                    {cta}
                </Stack>
            </Stack>
        </Stack>
    );
};
